import { resolveRuntimeApiBaseUrl, resolveRuntimeApiUrl } from './runtimeApiSupport';

export const DIAGRAM_STAGING_CONFIG_VERSION = 'ai-edu-v1-86-domain-runtime-2026-09-02';

export type DiagramRenderEvent = {
  event: 'ack' | 'status' | 'result' | 'error' | 'done';
  data: Record<string, unknown>;
};

export type DiagramRenderIdentity = {
  traceId: string;
  runId: string;
  nodeId: string;
  attempt: number;
};

export type DiagramRenderState = {
  status: 'idle' | 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  lastSequence: number;
  identity: DiagramRenderIdentity | null;
  stage: string | null;
  result: { artifactRef: string; fallbackImageRef: string | null } | null;
  errorCode: string | null;
  recoveryAction: string | null;
};

export const resolveDiagramApiBaseUrl = (mode: 'fixture' | 'staging', requestedBaseUrl?: string): string | null => {
  if (mode === 'fixture') {
    return null;
  }
  return resolveRuntimeApiBaseUrl(requestedBaseUrl);
};

export const createDiagramRenderState = (): DiagramRenderState => ({
  status: 'idle',
  lastSequence: 0,
  identity: null,
  stage: null,
  result: null,
  errorCode: null,
  recoveryAction: null,
});

export const createDiagramWriteHeaders = (idempotencyKey: string): Record<string, string> => ({
  'Idempotency-Key': idempotencyKey,
});

export const buildDiagramImageUrl = (baseUrl: string, imageRef: string): string =>
  resolveRuntimeApiUrl(baseUrl, `/api/uploads/${encodeURIComponent(imageRef)}`);

export const buildDiagramArtifactUrl = (_baseUrl: string, _artifactRef: string): null => null;

export const parseDiagramSseEvents = (rawStream: string): DiagramRenderEvent[] =>
  rawStream
    .trim()
    .split(/\r?\n\r?\n/)
    .filter(Boolean)
    .map((block) => {
      const lines = block.split(/\r?\n/);
      const eventLine = lines.find((line) => line.startsWith('event:'));
      const dataLine = lines.find((line) => line.startsWith('data:'));
      const event = eventLine?.slice('event:'.length).trim();
      const rawData = dataLine?.slice('data:'.length).trim();
      if (!event || !rawData || !['ack', 'status', 'result', 'error', 'done'].includes(event)) {
        throw new Error('Diagram render SSE event is invalid');
      }
      return { event: event as DiagramRenderEvent['event'], data: JSON.parse(rawData) as Record<string, unknown> };
    });

const readSequence = (data: Record<string, unknown>): number => {
  if (typeof data.sequence !== 'number' || !Number.isInteger(data.sequence) || data.sequence < 1) {
    throw new Error('Diagram render event sequence is invalid');
  }
  return data.sequence;
};

const readIdentity = (data: Record<string, unknown>): DiagramRenderIdentity => {
  if (
    typeof data.trace_id !== 'string' || !data.trace_id ||
    typeof data.run_id !== 'string' || !data.run_id ||
    typeof data.node_id !== 'string' || !data.node_id ||
    typeof data.attempt !== 'number' || !Number.isInteger(data.attempt) || data.attempt < 1
  ) {
    throw new Error('Diagram render event identity is invalid');
  }
  return { traceId: data.trace_id, runId: data.run_id, nodeId: data.node_id, attempt: data.attempt };
};

const identitiesMatch = (left: DiagramRenderIdentity, right: DiagramRenderIdentity): boolean =>
  left.traceId === right.traceId &&
  left.runId === right.runId &&
  left.nodeId === right.nodeId &&
  left.attempt === right.attempt;

const readResult = (data: Record<string, unknown>): DiagramRenderState['result'] => {
  const result = data.result;
  if (!result || typeof result !== 'object') {
    throw new Error('Diagram render result is missing');
  }
  const record = result as Record<string, unknown>;
  if (typeof record.artifact_ref !== 'string' || !record.artifact_ref) {
    throw new Error('Diagram render artifact reference is missing');
  }
  const fallback = record.fallback as Record<string, unknown> | undefined;
  return {
    artifactRef: record.artifact_ref,
    fallbackImageRef: fallback && typeof fallback.image_ref === 'string' ? fallback.image_ref : null,
  };
};

const isCancellationError = (data: Record<string, unknown>): boolean => {
  if (data.status === 'cancelled') {
    return true;
  }
  const error = data.error as Record<string, unknown> | undefined;
  return error?.code === 'cancelled' || error?.code === 'render_job_cancelled';
};

export const applyDiagramRenderEvent = (state: DiagramRenderState, event: DiagramRenderEvent): DiagramRenderState => {
  const sequence = readSequence(event.data);
  const identity = readIdentity(event.data);
  if (state.identity && !identitiesMatch(state.identity, identity)) {
    throw new Error('Diagram render event identity drift detected');
  }
  const nextIdentity = state.identity ?? identity;
  if (sequence !== state.lastSequence + 1) {
    throw new Error('Diagram render event sequence gap detected');
  }

  if (event.event === 'ack') {
    return { ...state, identity: nextIdentity, status: 'queued', lastSequence: sequence, errorCode: null, recoveryAction: null };
  }
  if (event.event === 'status') {
    return {
      ...state,
      identity: nextIdentity,
      status: event.data.status === 'cancelled' ? 'cancelled' : 'running',
      lastSequence: sequence,
      stage: typeof event.data.stage === 'string' ? event.data.stage : state.stage,
    };
  }
  if (event.event === 'result') {
    return { ...state, identity: nextIdentity, status: 'succeeded', lastSequence: sequence, result: readResult(event.data) };
  }
  if (event.event === 'error') {
    const error = event.data.error as Record<string, unknown> | undefined;
    return {
      ...state,
      identity: nextIdentity,
      status: isCancellationError(event.data) ? 'cancelled' : 'failed',
      lastSequence: sequence,
      errorCode: error && typeof error.code === 'string' ? error.code : 'unknown',
      recoveryAction: typeof event.data.recovery_action === 'string' ? event.data.recovery_action : null,
    };
  }
  return {
    ...state,
    identity: nextIdentity,
    status: event.data.status === 'cancelled' ? 'cancelled' : state.status,
    lastSequence: sequence,
  };
};
